import { useState, useMemo } from 'react';
import { ListChecks, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import type { Task, Priority } from '../types';
import type { SortField, SortDirection } from '../components/tasks/TasksTable';
import TasksStats from '../components/tasks/TasksStats';
import TasksFilters, { defaultFilters, type TaskFilters } from '../components/tasks/TasksFilters';
import TasksTable from '../components/tasks/TasksTable';
import ConfirmDialog from '../components/common/ConfirmDialog';

import { PageTitle } from '../../../../_metronic/layout/core';

const priorityOrder: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

function matchesDueDateRange(task: Task, range: TaskFilters['dueDateRange']): boolean {
  if (range === 'all') return true;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (range === 'no_date') return !task.dueDate;
  if (!task.dueDate) return false;

  const due = new Date(task.dueDate);
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  switch (range) {
    case 'overdue':
      return dueDay < today && task.status !== 'done';
    case 'today':
      return dueDay.getTime() === today.getTime();
    case 'this_week': {
      const weekEnd = new Date(today);
      weekEnd.setDate(today.getDate() + (7 - today.getDay()));
      return dueDay >= today && dueDay <= weekEnd;
    }
    case 'this_month':
      return due.getMonth() === now.getMonth() && due.getFullYear() === now.getFullYear();
    default:
      return true;
  }
}

export default function TasksPage() {
  const { state, apiDeleteTask, getBoardById } = useApp();

  const [filters, setFilters] = useState<TaskFilters>(defaultFilters);
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 25;

  const allStatuses = useMemo(() => {
    const set = new Set<string>();
    state.boards.forEach((b) => b.columns.forEach((c) => set.add(c.status)));
    return Array.from(set);
  }, [state.boards]);

  const filtered = useMemo(() => {
    const query = filters.search.toLowerCase();

    return state.tasks.filter((task) => {
      if (query && !task.title.toLowerCase().includes(query) && !task.description.toLowerCase().includes(query)) {
        return false;
      }
      if (filters.status.length > 0 && !filters.status.includes(task.status)) return false;
      if (filters.priority.length > 0 && !filters.priority.includes(task.priority)) return false;
      if (filters.boardIds.length > 0 && !filters.boardIds.includes(task.boardId)) return false;
      if (filters.assigneeIds.length > 0 && !filters.assigneeIds.some((id) => task.assigneeIds.includes(id))) {
        return false;
      }
      if (filters.labelIds.length > 0 && !filters.labelIds.some((id) => task.labelIds.includes(id))) {
        return false;
      }
      if (!matchesDueDateRange(task, filters.dueDateRange)) return false;
      if (filters.hasSubtasks === 'yes' && task.subtasks.length === 0) return false;
      if (filters.hasSubtasks === 'no' && task.subtasks.length > 0) return false;

      return true;
    });
  }, [state.tasks, filters]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'priority':
          cmp = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'dueDate':
          cmp = (a.dueDate || '9999').localeCompare(b.dueDate || '9999');
          break;
        case 'storyPoints':
          cmp = a.storyPoints - b.storyPoints;
          break;
        case 'createdAt':
          cmp = a.createdAt.localeCompare(b.createdAt);
          break;
        case 'board': {
          const boardA = getBoardById(a.boardId)?.name || '';
          const boardB = getBoardById(b.boardId)?.name || '';
          cmp = boardA.localeCompare(boardB);
          break;
        }
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDirection, getBoardById]);

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const paginatedTasks = sorted.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedIds.size === paginatedTasks.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedTasks.map((t) => t.id)));
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await Promise.all(Array.from(selectedIds).map((id) => apiDeleteTask(id)));
      setSelectedIds(new Set());
    } finally {
      setBulkDeleting(false);
      setShowBulkDelete(false);
    }
  };

  const handleFiltersChange = (next: TaskFilters) => {
    setFilters(next);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  return (
    <div className="p-4 md:p-6 space-y-5 mx-auto">
      <PageTitle>DevOps - Tarefas</PageTitle>
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-500 flex items-center justify-center">
            <ListChecks size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Tarefas</h1>
            <p className="text-sm text-slate-500">
              {filtered.length} {filtered.length === 1 ? 'tarefa encontrada' : 'tarefas encontradas'}
              {filtered.length !== state.tasks.length && ` de ${state.tasks.length} total`}
            </p>
          </div>
        </div>
      </header>

      <TasksStats tasks={state.tasks} />

      <TasksFilters filters={filters} onChange={handleFiltersChange} allStatuses={allStatuses} />

      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 animate-fade-in">
          <span className="text-sm font-medium text-brand-700">
            {selectedIds.size} {selectedIds.size === 1 ? 'tarefa selecionada' : 'tarefas selecionadas'}
          </span>
          <div className="flex-1" />
          <button
            onClick={() => setShowBulkDelete(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          >
            <Trash2 size={14} />
            Excluir
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
        </div>
      )}

      <TasksTable
        tasks={paginatedTasks}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-500">
            Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}
            {' '}-{' '}
            {Math.min(currentPage * ITEMS_PER_PAGE, sorted.length)} de {sorted.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                if (totalPages <= 7) return true;
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 1) return true;
                return false;
              })
              .map((page, idx, arr) => {
                const showEllipsis = idx > 0 && page - arr[idx - 1] > 1;
                return (
                  <span key={page} className="flex items-center">
                    {showEllipsis && <span className="px-1 text-slate-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-brand-500 text-white'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  </span>
                );
              })}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showBulkDelete}
        title="Excluir tarefas"
        message={`Tem certeza que deseja excluir ${selectedIds.size} tarefa${selectedIds.size > 1 ? 's' : ''}? Esta ação não pode ser desfeita.`}
        confirmLabel={bulkDeleting ? 'Excluindo...' : 'Excluir'}
        onConfirm={handleBulkDelete}
        onCancel={() => setShowBulkDelete(false)}
      />
    </div>
  );
}
